<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <v-card :class="compact && 'elevation-0'">
    <div class="d-flex justify-space-between align-center">
      <v-card-title :class="compact && 'pa-0 text-gray-900'" class="donation-flow__step-title" style="padding-top: 0px !important;">
        {{ t('donationGuide.summaryView.title') }}
      </v-card-title>
      <span class="step-indicator">{{ t('donationGuide.progress.mobileText', { current: 3, total: 3 }) }}</span>
    </div>
    <v-card-subtitle :class="compact && 'pt-8 pb-4 pl-0 summary-view-subtitle'" color="grey">
      {{ t('donationGuide.summaryView.subtitle') }}
    </v-card-subtitle>
    <v-card-text :class="compact && 'pa-0'">
      <v-form ref="form" v-model="valid" data-cy="summary-view-form" lazy-validation>
        <!-- Your gift card -->
        <div v-if="isMemoryGift" class="gift-card-summary mt-4">
          <p class="donation-flow__option-title mb-4" style="color: #4e5668 !important">
            {{ t('summary.giftCard.title') }}
          </p>

          <div
            class="gift-card-summary__card"
            data-cy="summary-view-gift-card"
            role="button"
            tabindex="0"
            :aria-label="t('aria.label.showPreview')"
            @click="openPreview"
            @keydown.enter="openPreview"
          >
            <!-- Swatch built from the gift card image's dominant colours -->
            <div class="gift-card-summary__swatch" :style="swatchStyle">
              <div class="gift-card-summary__swatch-inner" />
            </div>

            <!-- Title + contents -->
            <div class="gift-card-summary__body">
              <div class="gift-card-summary__title">
                {{ t('summary.giftCard.inMemoryOf', { name: giftCardRecipientName }) }}
              </div>
              <div class="gift-card-summary__contents">
                {{ giftCardContents }}
              </div>
            </div>

            <!-- Preview & edit action -->
            <div class="gift-card-summary__actions">
              <button
                type="button"
                class="gift-card-summary__action"
                data-cy="summary-view-gift-card-preview-btn"
                @click.stop="openPreview"
              >
                {{ t('summary.giftCard.previewAndEdit') }}
              </button>
            </div>
          </div>

          <v-alert
            v-model="previewError"
            border="start"
            type="error"
            class="mt-2"
            colored-border
            elevation="2"
          >
            {{ t('confirm.previewError') }}
          </v-alert>
        </div>

        <!-- Gift card preview modal -->
        <certificate-preview
          v-if="isMemoryGift"
          v-model="preview"
          :image-id="giftCardImageId"
          :image-url="giftCardImageUrl"
          :image-key="giftCardImageKey"
          :subject-name="giftCardRecipientName"
          :donation-cause-id="giftCardDonationCauseId"
          :message="giftCardGreeting"
          :donor-names="giftCardDonorNames"
          @close="preview = false"
          @edit="onPreviewEdit"
        />
        <!-- Donation Details Section -->
        <div v-if="showSummary && donation && donation.paymentSpecification" class="mt-8">
          <p class="donation-flow__option-title mb-4" style="color: #4e5668 !important">{{ t('donationGuide.summaryView.donationDetailsHeading') }}</p>

          <div class="donation-summary">
            <!-- Recipient -->
            <div class="donation-summary__row">
              <span class="donation-summary__label">{{ t('summary.recipient') }}</span>
              <span class="donation-summary__value donation-summary__value--grey">{{
                originalRecipientName
              }}</span>
            </div>
            <div class="donation-summary__row">
              <span class="donation-summary__label">{{ t('summary.charity') }}</span>
              <span class="donation-summary__value donation-summary__value--grey">{{
                donationRecipientName
              }}</span>
            </div>

            <v-divider class="my-6"/>

            <!-- Your Donation -->
            <div class="donation-summary__row">
              <span class="donation-summary__label">{{
                t('donationGuide.payment.summary.yourDonation')
              }}</span>
              <span class="donation-summary__value">{{
                formatCurrency(donation.paymentSpecification.causeAmount)
              }}</span>
            </div>

            <!-- Fee -->
            <div class="donation-summary__row">
              <span class="donation-summary__label">
                {{ t('donationGuide.payment.summary.fee') }}
                <v-tooltip location="top" :open-delay="0" :close-delay="1000" :max-width="300">
                  <template #activator="{ props }">
                    <v-icon v-bind="props" size="small" class="ml-1 fee-info-icon">mdi-information</v-icon>
                  </template>
                  <span>{{ t('donationDetails.form.adminFee.hint') }}</span>
                </v-tooltip>
              </span>
              <span class="donation-summary__value"
                >+ {{ formatCurrency(donation.paymentSpecification.adminAmount) }}</span
              >
            </div>
            <div class="donation-summary__row donation-summary__row--total">
              <span class="donation-summary__label total_amount">{{
                t('donationGuide.payment.summary.total')
              }}</span>
              <span class="donation-summary__value total_amount">{{ formatCurrency(totalAmount) }}</span>
            </div>
            <v-divider class="my-6"/>

            <!-- Payment method -->
            <div v-if="paymentMethodName" class="donation-summary__row">
              <span class="donation-summary__label">{{
                t('donationGuide.summaryView.paymentDetails.paymentMethod')
              }}</span>
              <span class="donation-summary__value donation-summary__value--grey">{{
                paymentMethodName
              }}</span>
            </div>

            <!-- Confirmation email -->
            <div v-if="confirmationEmail" class="donation-summary__row">
              <span class="donation-summary__label">{{
                t('donationGuide.summaryView.paymentDetails.confirmationTo')
              }}</span>
              <span class="donation-summary__value donation-summary__value--grey">{{
                confirmationEmail
              }}</span>
            </div>

            <!-- Delivery (always free) -->
            <div class="donation-summary__row">
              <span class="donation-summary__label">{{
                t('donationGuide.summaryView.paymentDetails.delivery')
              }}</span>
              <span class="donation-summary__value donation-summary__value--grey">{{
                t('donationGuide.summaryView.paymentDetails.free')
              }}</span>
            </div>
          </div>
        </div>

        <!-- <DonorSummaryComponent
          v-if="showSummary && donation && donation.donor"
          class="mt-5"
          :donor="donation.donor"
        /> -->

        

        <!-- Info about delivery -->
        <div v-if="isMemoryGift" class="delivery-info mt-6">
          <p class="delivery-info__title">{{ t('donationGuide.summaryView.deliveryInfo.title') }}</p>
          <div class="delivery-info__item">
            <v-icon class="delivery-info__icon" size="20">mdi-hand-heart</v-icon>
            <span>{{ t('donationGuide.summaryView.deliveryInfo.donation') }}</span>
          </div>
          <div class="delivery-info__item">
            <v-icon class="delivery-info__icon" size="20">mdi-email-outline</v-icon>
            <span>{{ t('donationGuide.summaryView.deliveryInfo.receipt') }}</span>
          </div>
          <div class="delivery-info__item">
            <v-icon class="delivery-info__icon" size="20">{{ deliveryInfoShippingIcon }}</v-icon>
            <span>{{ deliveryInfoShippingText }}</span>
          </div>
        </div>


        <!-- <v-checkbox
          v-if="!isMemoryGift"
          v-model="acceptPreview"
          class="mt-12"
          data-cy="summary-view-accept-preview-check"
          color="primary"
          :false-value="false"
          :true-value="true"
          :label="t('confirm.form.acceptPreview.alternate')"
          :rules="[rules.requiredCheck(t('validation.rule.acceptPreviewAlternate.label'))]"
        />
        <v-checkbox
          v-else
          v-model="acceptPreview"
          class="mt-12"
          data-cy="summary-view-accept-preview-check"
          color="primary"
          :false-value="false"
          :true-value="true"
          :label="t('confirm.form.acceptPreview.label')"
          :rules="[rules.requiredCheck(t('validation.rule.acceptPreview.label'))]"
        />
        <v-checkbox
          v-if="
            donation && donation.paymentSpecification && donation.paymentSpecification.paymentMethod
          "
          v-model="donation.sharePersonalInfoWithOrganization"
          data-cy="summary-view-share-personal-info-check"
          color="primary"
          :rules="[
            rules.requiredCheck(t('validation.rule.sharePersonalInfoWithOrganization.label')),
          ]"
        >
          <template #label>
            <div>
              {{ t('confirm.form.sharePersonalInfoWithOrganization.label') }}
              <v-tooltip v-model="privacyTooltipOpen" bottom>
                <template #activator="{ props }">
                  <a :href="privacyPolicyUrl" target="_blank" v-bind="props" @click.stop="onPrivacyClick">
                    {{ t('summary.privacyPolicy.link.text') }}
                  </a>
                </template>
                {{ t('summary.privacyPolicy.link.tooltip') }}
              </v-tooltip>
            </div>
          </template>
        </v-checkbox> -->
        <!-- <p class="mt-6 text-teal-600 font-weight-400">
          {{
            t('summary.deliveryInformation.text', {
              deliveryInformation: t('summary.deliveryInformation.sentToResponsibleFuneralHome'),
            })
          }}
        </p> -->
      </v-form>
      <v-alert
        v-model="submitError"
        border="start"
        type="error"
        variant="outlined"
        colored-border
        elevation="2"
        data-cy="payment-error-alert"
      >
        {{ t('confirm.submitError') }}
      </v-alert>
    </v-card-text>
    <donation-flow-buttons
      extra-class="mt-6"
      previous-id="summaryViewBackButton"
      next-id="summaryViewNextButton"
      previous-data-cy="summary-view-previous-btn"
      next-data-cy="summary-view-next-btn"
      :next-aria-label="t('aria.label.next') + ': ' + payText"
      :next-label="payText"
      :next-loading="submitting || loadingSendingSms"
      @previous-step="backButton"
      @next-step="submit"
    />
    <v-card-text class="mt-4 policy-text">
      {{ t('legal.policyText.intro') }}
      <a :href="privacyPolicyUrl" target="_blank" rel="noopener">{{ t('legal.policyText.privacyPolicyLink') }}</a>.
    </v-card-text>
    <paylink-error-retry-component
      :donation="donation"
      :show="sms.errorDialog"
      :message="sms.errorMessage"
      @toPaymentSelection="goToPaymentMethodSelection"
      @toSuccess="goToSuccessMethodSelection"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { ENDPOINTS, post, BASE_URL, guideErrorMessage, guideHttpStatus } from '@/api/mainApi';
import { RequestSession } from '@/interfaces/requestSession';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { Donation } from '@/interfaces/donation/donation';
import {
  isInvoice,
  isPayLink as isPayLinkFunc,
  isDigitalPayment as isDigitalPaymentFunc,
} from '@/classes/PaymentMethod';
import { Sms } from '@/interfaces/sms';

import { AttributeType } from '@/enums/attributeType';
import { isMemoryGift } from '@/classes/DonationType';
import { Type as DeliveryMethodType } from '@/classes/DeliveryMethod';
import DonorSummaryComponent from '@/components/summary/DonorSummaryComponent.vue';
import PaylinkErrorRetryComponent from '@/components/payment/PaylinkErrorRetryComponent.vue';
import CertificatePreview from '@/components/global/CertificatePreviewComponent.vue';
import DonationFlowButtons from '@/components/DonationFlowButtons.vue';
import { useImageOperations } from '@/composables/useImageOperations';
import { extractDominantColors } from '@/utils/colorExtraction';
import { useLabel } from '@/composables/useLabel';
import { useValidationRules } from '@/composables/useValidationRules';
import { useSmsOperations } from '@/composables/useSmsOperations';
import { getCurrencyForLocation } from '@/utils/currency';
import matomo from '@/services/matomoService';
import store from '@/store';

export default defineComponent({
  components: {
    DonorSummaryComponent,
    PaylinkErrorRetryComponent,
    CertificatePreview,
    DonationFlowButtons,
  },
  props: {
    intentText: {
      type: String,
      default: 'confirm.form.acceptReasonMemorial.label',
    },
    step: {
      type: Number,
      default: 0,
    },
    compact: Boolean,
  },
  emits: ['step', 'previousStep', 'nextStep'],
  setup(props, { emit }) {
    const { t } = useLabel();
    const { rules: baseRules } = useValidationRules(t);
    const { getLatestSmsStatus } = useSmsOperations();
    const { fetchImagesAndCategories } = useImageOperations();
    const rules = {
      requiredCheck: baseRules.value.requiredCheck,
    };

    const form = ref();
    const valid = ref(false);
    const preview = ref(false);
    const acceptPreview = ref(false);
    const acceptReason = ref(true);
    const paymentMethod = ref(null);
    const previewError = ref(false);
    const submitError = ref(false);
    const submitting = ref(false);
    const snackbar = ref(false);
    const showSummary = ref(true);
    const blankImageId = ref<number | null>(null);
    const dominantColors = ref<string[]>([]);
    const selectedImageUrl = ref('');
    const loadingSendingSms = ref(false);
    const sms = ref({
      errorDialog: false,
      errorMessage: '',
      smsValidationWaitTime: 4000,
    });
    const payText = ref(t('summary.proceedToPayment') as string);

    const getDonationStepFlowVersion = () => {
      return store.state.donationGuideVersion || DonationGuideVersion.V1;
    };

    const steps = computed(() => {
      return getDonationStepFlowVersion() == DonationGuideVersion.V1 ? stepV1 : stepV2;
    });
    const requestSession = computed(() => store.state.requestSession as RequestSession);
    const donation = computed(() => store.state.donation as Donation);
    const currentStep = computed(() => store.state.currentStep);
    const isMemoryGiftComputed = computed(() =>
      donation.value?.donationType?.type ? isMemoryGift(donation.value.donationType.type) : false
    );
    const hasAdminPercentage = computed(
      () =>
        !!donation.value &&
        !!donation.value.paymentSpecification &&
        !!donation.value.paymentSpecification.adminPercentage
    );

    const paymentMethodName = computed(() => {
      const method = donation.value?.paymentSpecification?.paymentMethod;
      if (!method) return '';
      return (
        (method.nameTranslationKey && (t(method.nameTranslationKey) as string)) ||
        method.name ||
        method.type ||
        ''
      );
    });

    const confirmationEmail = computed(() => donation.value?.donor?.email || '');

    const deliveryInfoKeyByType: Record<string, string> = {
      [DeliveryMethodType.EMAIL]: 'deliveryMethod.email.description',
      [DeliveryMethodType.POST]: 'deliveryMethod.post.description',
      [DeliveryMethodType.PRINT]: 'deliveryMethod.print.description',
      [DeliveryMethodType.EXTERNAL_AGENT]: 'deliveryMethod.external.description',
    };

    const deliveryInfoShippingText = computed(() => {
      const method = donation.value?.deliveryMethod;
      const key =
        method?.descriptionTranslationKey ||
        (method?.type ? deliveryInfoKeyByType[method.type] : '') ||
        'donationGuide.summaryView.deliveryInfo.printed';
      return t(key) || t('donationGuide.summaryView.deliveryInfo.printed');
    });

    const deliveryInfoShippingIcon = computed(() => {
      return donation.value?.deliveryMethod?.icon || 'mdi-image-outline';
    });

    const donationRecipientName = computed(() => {
      return (
        donation.value?.donationCause?.organizationGroup?.name ||
        donation.value?.donationCause?.name ||
        ''
      );
    });

    const donationCauseName = computed(() => {
      return donation.value?.donationCause?.name || '';
    });

    const privacyPolicyUrl = computed(() => {
      const location = store.state.userLocation || 'SWEDEN';
      let langCode = 'se';
      if (location == 'NORWAY') langCode = 'no';
      else if (location == 'GERMANY') langCode = 'de';
      return `${import.meta.env.BASE_URL}privacy/rapid_data_privacy_policy_${langCode}.pdf`;
    });
    const termsUrl = computed(() => {
      const location = store.state.userLocation || 'SWEDEN';
      let langCode = 'se';
      if (location == 'NORWAY') langCode = 'no';
      else if (location == 'GERMANY') langCode = 'de';
      return `${import.meta.env.BASE_URL}privacy/rapid_data_privacy_policy_${langCode}.pdf`;
    });
    const privacyTooltipOpen = ref(false);
    const onPrivacyClick = () => {
      // Close the tooltip when the link is clicked
      privacyTooltipOpen.value = false;
    };

    const goToPaymentMethodSelection = () => {
      emit('step', steps.value.PAYMENT);
      sms.value.errorDialog = false;
    };

    const getAttributeValueById = (id: number): string => {
      const attributeValue = donation.value?.attributeValues?.find((a) => a.attributeId === id);
      return attributeValue ? attributeValue.value : '';
    };

    const goToSuccessMethodSelection = () => {
      emit('step', steps.value.SUCCESS);
      sms.value.errorDialog = false;
    };

    const backButton = () => {
      emit('previousStep');
      matomo.trackEvent('checkout', 'donations_previous_clicked_checkout', 'donations_checkout_previous_clicked');
    };

    const submit = () => {
      if (!form.value.validate()) {
        return;
      }

      submitting.value = true;
      submitError.value = false;

      const timeout = setTimeout(() => (snackbar.value = true), 500);

      // Match live hotfix / Vue 2 required-check behaviour so Pennybridge
      // receives Anonymous: false and forwards donor data.
      if (!donation.value.sharePersonalInfoWithOrganization) {
        store.commit('updateDonation', { ...donation.value, sharePersonalInfoWithOrganization: true });
      }

      post(ENDPOINTS.DONATION, { ...donation.value, sharePersonalInfoWithOrganization: true })
        .then((data) => {
          // set request and donation from response
          store.commit('updateRequestSession', data.requestSession);
          store.commit('updateDonation', data.donation);
          matomo.trackEvent('donations_proceeded_to_payment', 'checkout', 'donations_proceeded_to_payment');

          // Handle redirect if paying with SwedbankPay/Ping/penny
          if (isDigitalPaymentComputed()) {
            // Checking if payment is digital
            matomo.trackEvent(
              'checkout',
              'donations_payment_method_selected_checkout',
              'donations_checkout_payment_method_selected'
            );
            emit('nextStep');
            matomo.navigateAfterTrack(data.requestSession.paymentResponse.redirectUrl);
          }
          if (isInvoicePayment()) {
            // go directly to the invoice step, because it is already 'paid'
            emit('step', steps.value.SUCCESS);
          }
          if (isPayLink()) {
            loadingSendingSms.value = true;

            // go directly to the success-view, because it shall be paid later
            // but first we verify that the sms was actually sent
            setTimeout(() => {
              getLatestSmsStatus(donation.value.serialnumber)
                .then((smsRes: Sms) => {
                  loadingSendingSms.value = false;

                  if (smsRes.errorMessage && smsRes.errorMessage.length > 0) {
                    window.log?.error('[Guide] checkout.paylinkSms — Paylink SMS reported an error', {
                      serialnumber: donation.value?.serialnumber,
                      smsErrorMessage: smsRes.errorMessage,
                      organizationGroupId: store.state.requestSession?.organizationGroup?.id,
                    });
                    sms.value.errorDialog = true;
                    sms.value.errorMessage = smsRes.errorMessage;
                  } else {
                    emit('step', steps.value.SUCCESS);
                  }
                })
                .catch((error) => {
                  window.log?.error('[Guide] checkout.paylinkSmsStatus — Failed to verify paylink SMS status', {
                    message: guideErrorMessage(error),
                    httpStatus: guideHttpStatus(error),
                    serialnumber: donation.value?.serialnumber,
                    organizationGroupId: store.state.requestSession?.organizationGroup?.id,
                  });
                  sms.value.errorDialog = true;
                  sms.value.errorMessage = t(
                    'payLinkSend.messages.payLinkSend.probableErrorCheckPhoneNumber'
                  ) as string;
                });
            }, sms.value.smsValidationWaitTime);
          }
        })
        .catch((error) => {
          window.log?.error('[Guide] checkout.createDonation — Payment failed during checkout', {
            message: guideErrorMessage(error),
            httpStatus: guideHttpStatus(error),
            paymentMethodType: donation.value?.paymentSpecification?.paymentMethod?.type,
            donationId: donation.value?.id,
            serialnumber: donation.value?.serialnumber,
            organizationGroupId: store.state.requestSession?.organizationGroup?.id,
          });
          submitError.value = true;
          matomo.trackEvent('donations_payment_error', 'checkout', 'donations_checkout_payment_error');
          store.commit('setSnackbar', {
            active: true,
            color: 'error',
            text: 'Payment submission failed',
          });
        })
        .finally(() => {
          clearTimeout(timeout);
          submitting.value = false;
        });
    };

    const isInvoicePayment = () => {
      return (
        donation.value &&
        donation.value.paymentSpecification &&
        donation.value.paymentSpecification.paymentMethod &&
        isInvoice(donation.value.paymentSpecification.paymentMethod.type)
      );
    };

    const isPayLink = () => {
      return (
        donation.value &&
        donation.value.paymentSpecification &&
        donation.value.paymentSpecification.paymentMethod &&
        isPayLinkFunc(donation.value.paymentSpecification.paymentMethod.type)
      );
    };

    const isDigitalPaymentComputed = () => {
      return (
        donation.value &&
        donation.value.paymentSpecification &&
        donation.value.paymentSpecification.paymentMethod &&
        isDigitalPaymentFunc(donation.value.paymentSpecification.paymentMethod.type)
      );
    };

    const isDirectPaymentAvailable = () => {
      return !!(
        requestSession.value &&
        requestSession.value.organizationGroup &&
        requestSession.value.organizationGroup.swedbankPaySettings &&
        requestSession.value.organizationGroup.swedbankPaySettings.id &&
        requestSession.value.organizationGroup.swedbankPaySettings.id > 0
      );
    };

    const setPayText = () => {
      if (isInvoicePayment()) {
        payText.value = t('confirm.invoice') as string;
      } else if (isPayLink()) {
        payText.value = t('confirm.paylink') as string;
      } else {
        payText.value = t('summary.proceedToPayment') as string;
      }
    };

    // ----- Gift card summary helpers -----

    const giftCardRecipientName = computed(
      () =>
        getAttributeValueById(AttributeType.SUBJECT_NAME) ||
        donation.value?.donationCase?.caseInfo?.name ||
        ''
    );
    const originalRecipientName = computed(() => {
      const fromDonationCase = donation.value?.donationCase?.caseInfo?.name?.trim();
      if (fromDonationCase) {
        return fromDonationCase;
      }
      const donationCases = requestSession.value?.donationCases || [];
      if (donationCases.length === 1) {
        return donationCases[0]?.caseInfo?.name?.trim() || '';
      }
      return giftCardRecipientName.value;
    });
    const giftCardGreeting = computed(() => getAttributeValueById(AttributeType.GREETING));
    const giftCardDonorNames = computed(() => getAttributeValueById(AttributeType.DONOR_NAME));
    const giftCardImageId = computed(() => donation.value?.donationImageId || null);
    const giftCardImageUrl = computed(() => donation.value?.customCertificateImageUrl || '');
    const giftCardImageKey = computed(() => donation.value?.imageKey || '');
    const giftCardDonationCauseId = computed(
      () =>
        donation.value?.donationCause?.id ||
        donation.value?.donationCase?.defaultDonationCauseIds?.[0] ||
        1
    );

    // Whether the user actually added a theme image (the "No Theme" blank doesn't count)
    const hasGiftCardImage = computed(() => {
      if (giftCardImageUrl.value) return true;
      const id = donation.value?.donationImageId;
      if (!id) return false;
      if (blankImageId.value != null && id === blankImageId.value) return false;
      return true;
    });

    // "Image · greeting · sender" — reflects what the user added, "Blank" if nothing
    const giftCardContents = computed(() => {
      const parts: string[] = [];
      if (hasGiftCardImage.value) parts.push(t('summary.giftCard.image') as string);
      if (giftCardGreeting.value?.trim()) parts.push(t('summary.giftCard.greeting') as string);
      if (giftCardDonorNames.value?.trim()) parts.push(t('summary.giftCard.sender') as string);
      if (parts.length === 0) return t('summary.giftCard.blank') as string;
      const joined = parts.join(' · ');
      return joined.charAt(0).toUpperCase() + joined.slice(1);
    });

    // Background for the gift card swatch.
    // - No image (toggled off / "No Theme" blank): plain grey → white gradient.
    // - With image: gradient built from the image's dominant colours, placed
    //   top-left → middle → bottom-right.
    const swatchStyle = computed(() => {
      if (!hasGiftCardImage.value || !dominantColors.value.length) {
        return { background: 'linear-gradient(135deg, #d1d5db 0%, #ffffff 100%)' };
      }
      const colors = dominantColors.value;
      const stops = colors.length === 1 ? `${colors[0]}, ${colors[0]}` : colors.join(', ');
      return { background: `linear-gradient(135deg, ${stops})` };
    });

    // Determine the "No Theme" image id so we can tell a blank card from a themed one.
    // Must mirror CertificateDetails.fetchImages exactly, otherwise the blank id won't
    // match donation.donationImageId and a blank card is mistaken for a themed one.
    const resolveBlankImageId = async () => {
      try {
        const orgGroupId =
          donation.value?.donationCause?.organizationGroup?.id ||
          requestSession.value?.organizationGroup?.id;
        const result = await fetchImagesAndCategories(orgGroupId);
        const all = (result.images || []) as Array<{
          id: number;
          url: string;
          donationImageCategory?: { name?: string };
        }>;

        const noTheme = all.find((img) => img.donationImageCategory?.name === 'No Theme');
        if (noTheme?.id != null) {
          blankImageId.value = noTheme.id;
        } else {
          // Fall back to the first visible themed image, same ordering as CertificateDetails
          const categoryOrder = ['Candles', 'Flowers', 'Nature', 'Landscape'];
          const visible = all
            .filter((img) => categoryOrder.includes(img.donationImageCategory?.name ?? ''))
            .sort(
              (a, b) =>
                categoryOrder.indexOf(a.donationImageCategory?.name ?? '') -
                categoryOrder.indexOf(b.donationImageCategory?.name ?? '')
            );
          blankImageId.value = visible.length > 0 ? visible[0].id : null;
        }

        // Resolve the raw URL of the user's selected image so the swatch gradient
        // is built from the image itself, not the composed certificate.
        const customUrl = donation.value?.customCertificateImageUrl;
        if (customUrl) {
          selectedImageUrl.value = customUrl.startsWith('http') || customUrl.startsWith('data:')
            ? customUrl
            : `${BASE_URL}${customUrl}`;
        } else {
          const selected = all.find((img) => img.id === donation.value?.donationImageId);
          if (selected?.url) {
            const base = !selected.url.startsWith('http') ? BASE_URL : '';
            selectedImageUrl.value = `${base}${selected.url}?compression=true`;
          } else {
            selectedImageUrl.value = '';
          }
        }
      } catch {
        blankImageId.value = null;
        selectedImageUrl.value = '';
      }
    };

    const loadDominantColors = async () => {
      // Only colour the swatch from a real theme image — a blank card keeps the grey → white gradient
      if (!hasGiftCardImage.value || !selectedImageUrl.value) return;
      const colors = await extractDominantColors(selectedImageUrl.value, 3);
      if (colors.length) {
        dominantColors.value = colors;
      }
    };

    const openPreview = () => {
      if (previewError.value) return;
      preview.value = true;
      matomo.trackEvent('checkout', 'gift_card_preview_clicked', 'donations_checkout_gift_card_preview_clicked');
    };

    const editGiftCard = () => {
      matomo.trackEvent('checkout', 'gift_card_edit_clicked', 'donations_checkout_gift_card_edit_clicked');
      emit('step', (steps.value as typeof stepV2).CERTIFICATE_DETAILS);
    };

    const onPreviewEdit = () => {
      preview.value = false;
      editGiftCard();
    };

    watch(
      currentStep,
      (val) => {
        showSummary.value = false;
        if (val == steps.value.SUMMARY) {
          matomo.trackEvent('checkout', 'viewed', 'donations_checkout_viewed');
          if (isMemoryGiftComputed.value) {
            // Clear any colours from a previous visit so the swatch starts neutral
            dominantColors.value = [];

            // Resolve the blank-image id first so hasGiftCardImage is accurate
            // before we decide whether to colour the swatch from the image
            resolveBlankImageId()
              .then(() => {
                showSummary.value = true;
                loadDominantColors();
              })
              .catch(() => {
                selectedImageUrl.value = '';
                showSummary.value = true;
              });
          } else {
            // Not a memory gift, show summary immediately
            showSummary.value = true;
          }
          setPayText();
        }
      },
      { immediate: true }
    );

    const totalAmount = computed(() => {
      if (!donation.value?.paymentSpecification) return 0;
      const causeAmount = donation.value.paymentSpecification.causeAmount || 0;
      const adminAmount = donation.value.paymentSpecification.adminAmount || 0;
      return causeAmount + adminAmount;
    });

    const formatCurrency = (amount: number | null) => {
      if (amount === null || amount === undefined) return '';
      const currency =
        donation.value?.paymentSpecification?.currencyCode ||
        getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN');
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'code',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    return {
      form,
      valid,
      preview,
      acceptPreview,
      acceptReason,
      paymentMethod,
      previewError,
      submitError,
      submitting,
      snackbar,
      showSummary,
      giftCardRecipientName,
      originalRecipientName,
      giftCardGreeting,
      giftCardDonorNames,
      giftCardImageId,
      giftCardImageUrl,
      giftCardImageKey,
      giftCardDonationCauseId,
      giftCardContents,
      swatchStyle,
      openPreview,
      editGiftCard,
      onPreviewEdit,
      loadingSendingSms,
      sms,
      payText,
      steps,
      requestSession,
      donation,
      currentStep,
      isMemoryGift: isMemoryGiftComputed,
      hasAdminPercentage,
      privacyPolicyUrl,
      termsUrl,
      privacyTooltipOpen,
      onPrivacyClick,
      rules,
      t,
      goToPaymentMethodSelection,
      getAttributeValueById,
      goToSuccessMethodSelection,
      backButton,
      submit,
      isInvoicePayment,
      isPayLink,
      isDigitalPayment: isDigitalPaymentComputed,
      isDirectPaymentAvailable,
      setPayText,
      totalAmount,
      formatCurrency,
      donationRecipientName,
      paymentMethodName,
      confirmationEmail,
      deliveryInfoShippingText,
      deliveryInfoShippingIcon,
    };
  },
});
</script>
<style scoped lang="scss">
.donation-summary {
  padding: 16px 0;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:last-child {
      margin-bottom: 0;
    }

    &--total {
      font-weight: 600;
      font-size: 16px;
      margin-top: 4px;
    }
  }

  &__label {
    display: flex;
    align-items: center;
    color: #4b5563;
    font-size: 14px;
    font-weight: 700;
  }

  &__value {
    color: #111928;
    font-size: 14px;
    font-weight: 400;
    font-family: 'lato', sans-serif;

    &--grey {
      color: #6b7280;
    }
  }
}

.donation-flow__option-title {
  font-weight: 700;
  font-size: 19px;
  color: #6B7280 !important;
}

.delivery-info {
  border-radius: 6px;
  padding: 24px 28px;
  background-color: #E6F5F5;

  &__title {
    font-family: 'Lato', sans-serif;
    font-weight: 700;
    font-size: 16px;
    color: #03543F !important;
    margin-bottom: 20px;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 14px;
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    color: #03543F;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 20px;
    height: 18px;
    margin-top: 2px;
    color: #03543F;
  }
}

.donation-flow {
  .table td:not(:first-child) {
    width: 99%;
  }

  .popup {
    margin: 12px;
  }

  .v-card__actions {
    padding-top: 36px;
    border-top: solid 1px var(--gray-150);
  }
}

:deep(.v-icon.mdi-information) {
  background-color: white !important;
  color: #6B7280 !important;
}

/* "Your gift card" preview card */
.gift-card-summary {
  &__card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    border: 1px solid #E5E7EB;
    border-radius: 14px;
    background-color: #fff;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover,
    &:focus-visible {
      border-color: #D1D5DB;
      box-shadow: 0 2px 8px rgba(17, 25, 40, 0.06);
      outline: none;
    }
  }

  &__swatch {
    position: relative;
    flex-shrink: 0;
    width: 40px;
    height: 56px;
    border-radius: 4px;
    overflow: hidden;
    background: linear-gradient(135deg, #d1d5db, #bcae95, #e8e0d2);
  }

  /* Lighter inner "label" box near the bottom of the swatch */
  &__swatch-inner {
    position: absolute;
    left: 14%;
    right: 12%;
    bottom: 10%;
    height: 28%;
    border-radius: 2px;
    background: #D1D5DB;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-family: 'Lato', sans-serif;
    font-weight: 700;
    font-size: 16px;
    color: #1F2A37;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__contents {
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    color: #6B7280;
    margin-top: 2px;
  }

  &__actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__action {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: 'Lato', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #047481;

    &:hover,
    &:focus-visible {
      color: #035c66;
      outline: none;
    }
  }

  &__action-divider {
    color: #D1D5DB;
    font-weight: 400;
  }
}

@media (min-width: 601px) {
  #summaryViewNextButton {
    width: 200px;
  }
}

.summary-view-subtitle {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
  opacity: 1;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: normal !important;
}

.donation-summary__label {
  font-weight: 700;
  font-family: "Lato", sans-serif;
  color: #1F2A37;
}

.donation-summary__row {
  margin-top: 5px;
}

.donation-summary__value {
  font-weight: 400;
  font-family: "Lato", sans-serif;
  color: #1F2A37;
  line-height: 155%;
}

.total_amount {
  font-weight: 800;;
}

.policy-text {
  font-family: "Lato", sans-serif;
  font-weight: 400;
  color: #6B7280;
  font-size: 15px;
}

.v-card-text a {
  text-decoration: underline !important;
}

</style>
